/** SUNTRONIX ONE — Public Delivery Confirmation.
 *  Unauthenticated page an employee opens from the one-tap link Suntronix
 *  Operations shares with them.  The employee can confirm the delivery
 *  address ops has, or propose a change (which becomes a review task —
 *  it never silently overwrites the live request).
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { AddressBlock, up } from "@/components/identity/Identity";
import { CheckCircle2, AlertTriangle, MapPin, Phone } from "lucide-react";

export default function PublicDeliveryConfirm() {
  const { token } = useParams();
  const [view, setView] = useState(null);
  const [error, setError] = useState(null);
  const [done, setDone] = useState(null);
  const [mode, setMode] = useState("view");   // view | propose
  const [busy, setBusy] = useState(false);
  const [address, setAddress] = useState({});
  const [primary, setPrimary] = useState({});
  const [secondary, setSecondary] = useState({});
  const [note, setNote] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/public/delivery/${token}`);
        setView(data);
        setAddress(data.delivery_details.address || {});
        setPrimary(data.delivery_details.primary_contact || {});
        setSecondary(data.delivery_details.secondary_contact || {});
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is invalid or expired.");
      }
    })();
  }, [token]);

  const submit = async (decision) => {
    setBusy(true);
    try {
      const payload = { decision };
      if (decision === "propose_change") {
        payload.proposed_address = address;
        payload.proposed_primary_contact = primary;
        payload.proposed_secondary_contact = secondary;
        payload.note = note;
      }
      const { data } = await api.post(`/public/delivery/${token}/confirm`, payload);
      setDone(data.status);
      toast.success(decision === "confirm" ? "Thanks — confirmed." : "Change proposed — Suntronix will review.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to submit");
    } finally { setBusy(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="max-w-md w-full bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="public-error">
          <AlertTriangle className="h-8 w-8 text-amber-600 mx-auto" strokeWidth={1.5} />
          <div className="mt-3 text-sm text-slate-700">{error}</div>
        </div>
      </div>
    );
  }
  if (!view) {
    return <div className="min-h-screen flex items-center justify-center text-sm text-slate-500" data-testid="public-loading">Loading…</div>;
  }
  if (done) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-slate-50">
        <div className="max-w-md w-full bg-white border border-emerald-200 rounded-lg p-6 text-center" data-testid="public-done">
          <CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto" strokeWidth={1.5} />
          <div className="mt-3 text-base font-medium text-slate-900">
            {done === "confirmed" ? "Delivery details confirmed." : "Change submitted for review."}
          </div>
          <div className="mt-2 text-[12px] text-slate-500">You can close this page.</div>
        </div>
      </div>
    );
  }

  const dd = view.delivery_details || {};
  return (
    <div className="min-h-screen bg-slate-50 p-4">
      <div className="max-w-md mx-auto bg-white border border-slate-200 rounded-lg p-5" data-testid="public-confirm">
        <div className="text-[11px] uppercase tracking-wider text-slate-500">SUNTRONIX ONE</div>
        <div className="mt-1 text-lg font-medium text-slate-900">Please confirm your delivery details</div>
        <div className="mt-1 text-[12px] text-slate-500">{view.request.customer} · Request {view.request.code || view.request.id?.slice(0, 8)}</div>

        {mode === "view" && (
          <div className="mt-5 space-y-3">
            <div className="rounded-md border border-slate-200 p-3 bg-slate-50 text-[13px]" data-testid="public-address">
              <div className="flex items-start gap-2">
                <MapPin className="h-4 w-4 text-slate-500 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-slate-800">Delivery address</div>
                  {dd.address?.address_line1 || dd.address?.city ? <AddressBlock street={[dd.address?.address_line1, dd.address?.address_line2].filter(Boolean).join("\n")} city={dd.address?.city} pin={dd.address?.postal_code} state={dd.address?.state} country={dd.address?.country} className="text-slate-700 mt-0.5" testid="public-address-block" /> : <div className="text-slate-700 mt-0.5">Not set</div>}
                </div>
              </div>
            </div>
            <div className="rounded-md border border-slate-200 p-3 bg-slate-50 text-[13px]" data-testid="public-primary">
              <div className="flex items-start gap-2">
                <Phone className="h-4 w-4 text-slate-500 mt-0.5" strokeWidth={1.5} />
                <div>
                  <div className="font-medium text-slate-800">Primary contact</div>
                  <div className="text-slate-700 mt-0.5">
                    {dd.primary_contact?.name ? <b>{up(dd.primary_contact.name)}</b> : view.employee_email} · {dd.primary_contact?.number || "—"}
                  </div>
                </div>
              </div>
            </div>
            {dd.secondary_contact?.number && (
              <div className="rounded-md border border-slate-200 p-3 bg-slate-50 text-[13px]" data-testid="public-secondary">
                <div className="flex items-start gap-2">
                  <Phone className="h-4 w-4 text-slate-500 mt-0.5" strokeWidth={1.5} />
                  <div>
                    <div className="font-medium text-slate-800">Secondary contact</div>
                    <div className="text-slate-700 mt-0.5">
                      {dd.secondary_contact.name ? <b>{up(dd.secondary_contact.name)}</b> : "—"} · {dd.secondary_contact.number}
                      {dd.secondary_contact.relationship ? ` · ${dd.secondary_contact.relationship}` : ""}
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div className="pt-2 flex flex-col gap-2">
              <Button onClick={() => submit("confirm")} disabled={busy} data-testid="public-btn-confirm"
                className="w-full" style={{ background: "var(--st-accent, #B7803F)", color: "white" }}>
                {busy ? "Sending…" : "These details are correct"}
              </Button>
              <Button variant="outline" onClick={() => setMode("propose")} data-testid="public-btn-propose" className="w-full">
                Suggest a change
              </Button>
            </div>
          </div>
        )}

        {mode === "propose" && (
          <div className="mt-5 space-y-3" data-testid="public-propose-form">
            <PublicField label="Address line 1" value={address.address_line1}
              onChange={(v) => setAddress({ ...address, address_line1: v })} testId="prop-line1" />
            <PublicField label="Address line 2" value={address.address_line2}
              onChange={(v) => setAddress({ ...address, address_line2: v })} testId="prop-line2" />
            <div className="grid grid-cols-2 gap-2">
              <PublicField label="City" value={address.city}
                onChange={(v) => setAddress({ ...address, city: v })} testId="prop-city" />
              <PublicField label="Postal code" value={address.postal_code}
                onChange={(v) => setAddress({ ...address, postal_code: v })} testId="prop-postal" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <PublicField label="State" value={address.state}
                onChange={(v) => setAddress({ ...address, state: v })} testId="prop-state" />
              <PublicField label="Country" value={address.country}
                onChange={(v) => setAddress({ ...address, country: v })} testId="prop-country" />
            </div>
            <PublicField label="Primary contact number" value={primary.number}
              onChange={(v) => setPrimary({ ...primary, number: v })} testId="prop-primary" />
            <PublicField label="Secondary contact number (optional)" value={secondary.number}
              onChange={(v) => setSecondary({ ...secondary, number: v })} testId="prop-secondary" />
            <div>
              <Label className="text-[11px] text-slate-600">Notes (optional)</Label>
              <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="prop-note" />
            </div>
            <div className="pt-2 flex gap-2">
              <Button variant="outline" onClick={() => setMode("view")} className="flex-1">Back</Button>
              <Button onClick={() => submit("propose_change")} disabled={busy} data-testid="public-btn-submit-change"
                className="flex-1" style={{ background: "var(--st-accent, #B7803F)", color: "white" }}>
                {busy ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          </div>
        )}

        <div className="mt-6 text-[10px] text-slate-400 text-center">
          Link expires on {view.invite?.expires_at ? new Date(view.invite.expires_at).toLocaleDateString() : "—"}
        </div>
      </div>
    </div>
  );
}

function PublicField({ label, value, onChange, testId }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-600">{label}</Label>
      <Input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} />
    </div>
  );
}
